import cn from 'classnames'
import {FC, ReactNode, useId, useState} from 'react'

import {FormTooltipModal} from '@/components/common/form/tooltip/FormTooltipModal'
import {QuestionMarkIcon} from '@/components/common/icons/QuestionMarkIcon'
import {getCommonContent} from '@/content/commonContent'
import {useTranslation} from '@/i18n'
import {HOVER_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Text, TextVariant} from '../../content/Text'

type FormTooltipProps = {
  tooltipText: ReactNode
  hasIcon?: boolean
  icon?: string | ReactNode
  backgroundColor?: string
  textColor?: string
  position?: 'left' | 'right' | 'center'
}

export const FormTooltip: FC<React.PropsWithChildren<FormTooltipProps>> = ({
  tooltipText,
  hasIcon = true,
  icon,
  backgroundColor = theme.palette.tertiary.main,
  textColor = theme.palette.text.white,
  position = 'left',
}) => {
  const {t} = useTranslation()
  const {learnMoreText} = getCommonContent()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const tooltipId = useId()

  const tooltipIcon = icon || (
    <QuestionMarkIcon
      backgroundColor={theme.palette.gray[600]}
      borderColor={theme.palette.gray[600]}
      color={theme.palette.text.white}
    />
  )

  const openModal = (e: any) => {
    e.preventDefault()
    e.stopPropagation()
    setIsModalVisible(true)
  }

  return (
    <>
      <span className="tooltip desktop">
        <button
          type="button"
          className="trigger"
          aria-label={hasIcon ? t(learnMoreText) : undefined}
          aria-describedby={tooltipId}
        >
          {hasIcon ? (
            tooltipIcon
          ) : (
            <Text variant={TextVariant.text14} element="span" className="underline">
              {t(learnMoreText)}
            </Text>
          )}
        </button>
        <span id={tooltipId} role="tooltip" className={cn('tooltip-text', position)}>
          <Text variant={TextVariant.caption} color={textColor} className="text-block">
            {tooltipText}
          </Text>
        </span>
      </span>
      <button
        type="button"
        className="tooltip mobile trigger"
        onClick={openModal}
        aria-label={hasIcon ? t(learnMoreText) : undefined}
      >
        {hasIcon ? (
          tooltipIcon
        ) : (
          <Text variant={TextVariant.text14} element="span" className="underline">
            {t(learnMoreText)}
          </Text>
        )}
      </button>
      <span className="mobile">
        <FormTooltipModal
          tooltipText={tooltipText}
          isVisible={isModalVisible}
          onClose={() => setIsModalVisible(false)}
        />
      </span>

      <style jsx>{`
        .tooltip {
          position: relative;
          display: inline-block;
          vertical-align: middle;
          font-weight: ${theme.fontWeight.regular};
          font-style: normal;
          white-space: normal;
          text-transform: none;
          line-height: 20px;
          cursor: pointer;
        }
        .trigger {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: ${theme.spacing[1]};
          margin: -${theme.spacing[1.5]} -${theme.spacing[1]} -${theme.spacing[1]};
          vertical-align: middle;
        }
        .tooltip-text {
          background-color: ${backgroundColor};
          width: 285px;
          position: absolute;
          bottom: 26px;
          left: calc(50% - 5px);
          text-align: left;
          padding: ${theme.spacing[2]};
          margin-left: -${theme.spacing[4]};
          white-space: normal;
          visibility: hidden;
          opacity: 0;
          border-radius: 4px;
          transition: opacity ${HOVER_TRANSITION}ms ease-in-out;
        }
        .tooltip-text::before {
          content: '';
          position: absolute;
          bottom: -18px;
          left: 17px;
          border-width: 12px;
          border-style: solid;
          border-color: ${backgroundColor} transparent transparent transparent;
          pointer-events: none;
        }
        .tooltip:hover .tooltip-text,
        .trigger:focus-visible + .tooltip-text {
          visibility: visible;
          opacity: 1;
          z-index: 1;
        }
        .mobile {
          display: none;
        }
        .tooltip-text.right {
          left: unset;
          margin-left: 0;
          right: calc(50% - 5px);
          margin-right: -30px;
        }
        .tooltip-text.right::before {
          left: unset;
          right: 23px;
        }
        .tooltip-text.center {
          left: 50%;
          transform: translateX(-50%);
          margin: 0;
        }
        .tooltip-text.center::before {
          left: 50%;
          transform: translateX(-50%);
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .desktop {
            display: none;
          }
          .mobile {
            display: inline-block;
          }
        }
      `}</style>
    </>
  )
}
